
import streetDataWithArrondissement from '../data/street_names_with_arrondissement.json';

// Normalize by stripping accents for search while keeping original casing for display
const removeAccents = (str) => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

// Normalize arrondissement names by replacing various dashes with hyphens
const normalizeArrondissementName = (name) => {
  if (!name) return '';
  // Replace en-dash, em-dash, and other dash variants with regular hyphen
  return name.replace(/[–—−]/g, '-');
};

/**
 * Service to handle street name data
 */
export const StreetNameService = {
  /**
   * Get all street names from the JSON data
   * @param {string} arrondissementName - Optional arrondissement name to filter by
   * @returns {Array<{label: string, searchKey: string}>} Array of street name objects
   */
  getAllStreetNames: (arrondissementName = null) => {
    if (!Array.isArray(streetDataWithArrondissement)) {
      console.error('Street name data is not properly structured');
      return [];
    }

    // If no arrondissement specified, return all streets
    if (!arrondissementName) {
      return streetDataWithArrondissement;
    }

    // Normalize the arrondissement name for comparison
    const normalizedArrondissement = normalizeArrondissementName(arrondissementName);

    // Filter by arrondissement (include N/A entries as well for cross-borough streets)
    return streetDataWithArrondissement.filter(item => {
      const itemArrondissement = normalizeArrondissementName(item.arrondissement);
      return itemArrondissement === normalizedArrondissement || item.arrondissement === 'N/A';
    });
  },

  /**
   * Filter street names based on input
   * @param {string} input - User input to filter by
   * @param {number} minChars - Minimum characters before filtering
   * @param {number} maxResults - Maximum number of results to return
   * @param {string} arrondissementName - Optional arrondissement name to filter by
   * @returns {Array<string>} Filtered array of street names
   */
  filterStreetNames: (input, minChars = 2, maxResults = 10, arrondissementName = null) => {
    // Don't filter if input doesn't meet minimum character requirement
    if (!input || input.length < minChars) {
      return [];
    }

    const allNames = StreetNameService.getAllStreetNames(arrondissementName);
    const normalizedInput = removeAccents(input.toUpperCase());

    // Filter names that include the input string (case/accents insensitive)
    const filteredNames = allNames.filter(item => {
      const searchKey = item.searchKey || removeAccents(String(item.label || item).toUpperCase());
      return searchKey.includes(normalizedInput);
    });

    // Remove duplicates (same label can appear for multiple arrondissements)
    const uniqueLabels = [...new Set(filteredNames.map(item => item.label || item))];

    // Return limited results
    return uniqueLabels.slice(0, maxResults);
  }
};
